import { FC, useState, useEffect, useRef, MutableRefObject } from 'react';
import { Box } from '@doda/io-react/common';
import { DisplayJobCount } from 'components/DisplayJobCount';
import classnames from 'classnames';
import styles from './index.module.scss';
import { ACCORDION_ITEMS, NaviItem } from './constants';
import { NavigationListItem } from './components/NavigationListItem';
import { Logo } from './components/Logo';
import { AccordionListWrapper } from './components/AccordionListWrapper';
import { HistoryJobs } from './components/HistoryJobs';
import { SearchedConditions } from './components/SearchedConditions';
import { BookmarkList } from './components/BookmarkList';
import { SavedSearches } from './components/SavedSearches';
import { useOnScroll } from './hooks/useOnScroll';
import { useSearchedCondition } from './hooks/useSearchedCondition';
import { useBookmarkList } from './hooks/useBookmarkList';
import { useSavedSearchesList } from './hooks/useSavedSearchesList';
import { useHistoryJobs } from './hooks/useHistoryJobs';

/**
 * 追従検索結果ナビゲーション
 * @param loginStatus ログイン状態
 * @param totalCount metaData.totalCount（例：求人一覧取得APIレスポンス）
 * @param resultCount metaData.resultCount（例：求人一覧取得APIレスポンス）
 * @param offset metaData.offset（例：求人一覧取得APIレスポンス）
 */
interface Props {
  loginStatus: boolean;
  totalCount: number;
  resultCount: number;
  offset: number;
}

export const FollowSearchResultsNavigation: FC<Props> = ({
  loginStatus,
  totalCount,
  resultCount,
  offset,
}) => {
  const { scrolled: isShowHeader } = useOnScroll(100);
  const [menuContents, setMenuContents] = useState<number | null>(null);
  const [isOpenMenu, setIsOpenMenu] = useState(false);

  const menuRef: MutableRefObject<HTMLDivElement | null> = useRef(null);
  const menuListRef: MutableRefObject<HTMLUListElement | null> = useRef(null);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (
        !menuRef?.current?.contains(e.target as Node) &&
        !menuListRef?.current?.contains(e.target as Node)
      ) {
        onClickMenuClose();
      }
    };

    document.addEventListener('click', onClickOutside);
    return () => {
      document.removeEventListener('click', onClickOutside);
    };
  }, []);

  // アコーディオンメニュー開閉
  const onClickMenuOpen = (item: NaviItem) => {
    if (isOpenMenu && item.id !== menuContents) {
      setIsOpenMenu(false);
      // CSSで設定しているアニメーションの秒数分実行を遅らせる
      setTimeout(() => {
        setMenuContents(item.id);
        setIsOpenMenu(true);
      }, 300);
      return;
    }
    setIsOpenMenu(!isOpenMenu);
    setMenuContents(item.id);
  };

  // アコーディオンメニュー閉じるボタン
  const onClickMenuClose = () => {
    setIsOpenMenu(false);
  };

  // 最近見た求人
  const { historyList, updateHistoryList, onClickHistory } = useHistoryJobs({
    isOpenMenu,
    menuContents,
    onClickMenuOpen,
  });

  // 気になるリスト
  const { bookmarkList, onClickBookmarkList } = useBookmarkList({
    isOpenMenu,
    menuContents,
    onClickMenuOpen,
  });

  // 最近検索した条件
  const { searchConditionList, updateSearchConditionList } =
    useSearchedCondition();

  // 保存した検索条件
  const { savedSearchesList, onClickSavedSearches } = useSavedSearchesList(
    loginStatus,
    onClickMenuOpen
  );

  return (
    <Box
      className={classnames(styles.followSearchResultsNavigation, {
        [styles.is_show]: isShowHeader,
      })}
      color="white"
      boxShadow="blue"
    >
      <div className={styles.followSearchResultsNavigation__inner}>
        <Logo loginStatus={loginStatus} />
        <div className={styles.followSearchResultsNavigation__displayJobCount}>
          <DisplayJobCount
            totalCount={totalCount}
            resultCount={resultCount}
            offset={offset}
          />
        </div>
        <nav>
          <ul
            className={styles.followSearchResultsNavigation__lists}
            ref={menuListRef}
          >
            <NavigationListItem
              item={ACCORDION_ITEMS.historyJobs}
              title={ACCORDION_ITEMS.historyJobs.label}
              menuContents={menuContents}
              isOpenMenu={isOpenMenu}
              onClickMenuOpen={onClickHistory}
            />
            <NavigationListItem
              item={ACCORDION_ITEMS.searchedConditions}
              title={ACCORDION_ITEMS.searchedConditions.label}
              menuContents={menuContents}
              isOpenMenu={isOpenMenu}
              onClickMenuOpen={onClickMenuOpen}
            />
            <NavigationListItem
              item={ACCORDION_ITEMS.bookmarkList}
              title={ACCORDION_ITEMS.bookmarkList.label}
              menuContents={menuContents}
              isOpenMenu={isOpenMenu}
              onClickMenuOpen={onClickBookmarkList}
            />
            {loginStatus && (
              <NavigationListItem
                item={ACCORDION_ITEMS.savedSearches}
                title={ACCORDION_ITEMS.savedSearches.label}
                menuContents={menuContents}
                isOpenMenu={isOpenMenu}
                onClickMenuOpen={onClickSavedSearches}
              />
            )}
          </ul>
        </nav>
      </div>
      <div ref={menuRef}>
        <AccordionListWrapper isOpenMenu={isOpenMenu}>
          {menuContents === ACCORDION_ITEMS.historyJobs.id && (
            <HistoryJobs
              historyList={historyList}
              onClickMenuClose={onClickMenuClose}
              updateHistoryList={updateHistoryList}
              loginStatus={loginStatus}
            />
          )}
          {menuContents === ACCORDION_ITEMS.searchedConditions.id && (
            <SearchedConditions
              searchConditionList={searchConditionList}
              updateSearchConditionList={updateSearchConditionList}
              onClickMenuClose={onClickMenuClose}
              loginStatus={loginStatus}
            />
          )}
          {menuContents === ACCORDION_ITEMS.bookmarkList.id && (
            <BookmarkList
              bookmarkList={bookmarkList}
              onClickMenuClose={onClickMenuClose}
              loginStatus={loginStatus}
            />
          )}
          {menuContents === ACCORDION_ITEMS.savedSearches.id && (
            <SavedSearches
              savedSearchesList={savedSearchesList}
              onClickMenuClose={onClickMenuClose}
              loginStatus={loginStatus}
            />
          )}
        </AccordionListWrapper>
      </div>
    </Box>
  );
};
